import React, { createContext, useContext, useReducer, useCallback, useMemo, useEffect } from 'react';
import services from '../services';
import { characterService } from '../services/characterService';
import { logger } from '../services/utils';

// Create context
export const CharacterContext = createContext();

// Action types for reducer
const CharacterActionTypes = {
  SET_CHARACTERS: 'SET_CHARACTERS',
  ADD_CHARACTER: 'ADD_CHARACTER',
  UPDATE_CHARACTER: 'UPDATE_CHARACTER',
  SET_SELECTED_CHARACTERS: 'SET_SELECTED_CHARACTERS',
  TOGGLE_CHARACTER_SELECTION: 'TOGGLE_CHARACTER_SELECTION',
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  RESET: 'RESET'
};

// Initial state
const initialState = {
  characters: [],
  selectedCharacters: [],
  isLoading: false,
  error: null
};

// Reducer function
function characterReducer(state, action) {
  switch (action.type) {
    case CharacterActionTypes.SET_CHARACTERS:
      return {
        ...state,
        characters: action.payload
      };
    case CharacterActionTypes.ADD_CHARACTER: {
      const newCharacter = action.payload;
      return {
        ...state,
        characters: [...state.characters, newCharacter]
      };
    }
    case CharacterActionTypes.UPDATE_CHARACTER: {
      const updatedCharacter = action.payload;
      return {
        ...state,
        characters: state.characters.map(char => 
          char.id === updatedCharacter.id ? updatedCharacter : char
        ),
        selectedCharacters: state.selectedCharacters.map(char => 
          char.id === updatedCharacter.id ? updatedCharacter : char
        )
      };
    }
    case CharacterActionTypes.SET_SELECTED_CHARACTERS:
      return {
        ...state,
        selectedCharacters: action.payload
      };
    case CharacterActionTypes.TOGGLE_CHARACTER_SELECTION: {
      const character = action.payload;
      const isSelected = state.selectedCharacters.some(c => c.id === character.id);
      
      if (isSelected) {
        // Remove from selection
        return {
          ...state,
          selectedCharacters: state.selectedCharacters.filter(c => c.id !== character.id)
        };
      } else {
        // Add to selection (if less than 3 characters are selected)
        if (state.selectedCharacters.length < 3) {
          return {
            ...state,
            selectedCharacters: [...state.selectedCharacters, character]
          };
        }
      }
      return state; // No change if already 3 selected
    }
    case CharacterActionTypes.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload
      };
    case CharacterActionTypes.SET_ERROR:
      return {
        ...state,
        error: action.payload
      };
    case CharacterActionTypes.RESET:
      return initialState;
    default:
      return state;
  }
}

export function CharacterProvider({ children }) {
  const [state, dispatch] = useReducer(characterReducer, initialState);
  const { characters, selectedCharacters, isLoading, error } = state;

  // Fetch all characters
  const fetchCharacters = useCallback(async () => {
    dispatch({ type: CharacterActionTypes.SET_LOADING, payload: true });
    
    try {
      logger.info('CharacterContext', 'Fetching characters using characterService');
      const characters = await characterService.getCharacters();
      logger.debug('CharacterContext', `Retrieved ${characters?.length || 0} characters`);
      dispatch({ type: CharacterActionTypes.SET_CHARACTERS, payload: characters });
      dispatch({ type: CharacterActionTypes.SET_ERROR, payload: null });
    } catch (error) {
      logger.error('CharacterContext', 'Failed to fetch characters:', error);
      dispatch({ 
        type: CharacterActionTypes.SET_ERROR, 
        payload: 'Failed to fetch characters. Please try again.'
      });
    } finally {
      dispatch({ type: CharacterActionTypes.SET_LOADING, payload: false });
    }
  }, []);

  // Fetch all characters on component mount
  useEffect(() => {
    fetchCharacters();
  }, [fetchCharacters]);
  
  // Listen for force reset event (like logout)
  useEffect(() => {
    const handleForceReset = (event) => {
      logger.info('CharacterContext', 'Received force reset event:', event.detail);
      
      // Reset character state
      dispatch({ type: CharacterActionTypes.RESET });
      
      logger.info('CharacterContext', 'All character state has been reset');
    };
    
    // Add event listener
    document.addEventListener('forceResetAllContexts', handleForceReset);
    
    // Cleanup on unmount
    return () => {
      document.removeEventListener('forceResetAllContexts', handleForceReset);
    };
  }, []);

  // Create a new character
  const createCharacter = useCallback(async (characterData) => {
    dispatch({ type: CharacterActionTypes.SET_LOADING, payload: true });
    
    try {
      logger.info('CharacterContext', 'Creating new character using characterService');
      const newCharacter = await characterService.createCharacter(characterData);
      logger.debug('CharacterContext', 'Character created successfully', newCharacter);
      
      dispatch({ type: CharacterActionTypes.ADD_CHARACTER, payload: newCharacter });
      dispatch({ 
        type: CharacterActionTypes.SET_SELECTED_CHARACTERS, 
        payload: [...selectedCharacters, newCharacter]
      });
      dispatch({ type: CharacterActionTypes.SET_ERROR, payload: null });
      
      return newCharacter;
    } catch (error) {
      logger.error('CharacterContext', 'Failed to create character:', error);
      dispatch({ 
        type: CharacterActionTypes.SET_ERROR, 
        payload: 'Failed to create character. Please try again.'
      });
      throw error;
    } finally {
      dispatch({ type: CharacterActionTypes.SET_LOADING, payload: false });
    }
  }, [selectedCharacters]);

  // Update an existing character
  const updateCharacter = useCallback(async (characterId, characterData) => {
    dispatch({ type: CharacterActionTypes.SET_LOADING, payload: true });
    
    try {
      logger.info('CharacterContext', `Updating character ${characterId} using characterService`);
      const updatedCharacter = await characterService.updateCharacter(characterId, characterData);
      logger.debug('CharacterContext', 'Character updated successfully', updatedCharacter);
      
      dispatch({ type: CharacterActionTypes.UPDATE_CHARACTER, payload: updatedCharacter });
      dispatch({ type: CharacterActionTypes.SET_ERROR, payload: null });
      
      return updatedCharacter;
    } catch (error) {
      logger.error('CharacterContext', `Failed to update character ${characterId}:`, error);
      dispatch({ 
        type: CharacterActionTypes.SET_ERROR, 
        payload: 'Failed to update character. Please try again.'
      });
      throw error;
    } finally {
      dispatch({ type: CharacterActionTypes.SET_LOADING, payload: false });
    }
  }, []);

  // Toggle character selection
  const toggleCharacterSelection = useCallback((character) => {
    dispatch({ 
      type: CharacterActionTypes.TOGGLE_CHARACTER_SELECTION, 
      payload: character 
    });
  }, []);

  // Create memoized context value
  const contextValue = useMemo(() => ({
    // State
    characters,
    selectedCharacters,
    isLoading,
    error,
    
    // Actions
    fetchCharacters,
    createCharacter,
    updateCharacter,
    toggleCharacterSelection,
    setError: (errorMessage) => dispatch({ 
      type: CharacterActionTypes.SET_ERROR, 
      payload: errorMessage 
    }),
    resetCharacters: () => dispatch({ type: CharacterActionTypes.RESET })
  }), [
    characters, selectedCharacters, isLoading, error,
    fetchCharacters, createCharacter, updateCharacter, toggleCharacterSelection
  ]);

  return (
    <CharacterContext.Provider value={contextValue}>
      {children}
    </CharacterContext.Provider>
  );
}

// Custom hook for using the entire character context
export function useCharacters() {
  const context = useContext(CharacterContext);
  if (context === undefined) {
    throw new Error('useCharacters must be used within a CharacterProvider');
  }
  return context;
}

// Custom hook for accessing character list
export function useCharacterList() {
  const { characters, isLoading, error } = useCharacters();
  return { characters, isLoading, error };
}

// Custom hook for accessing selected characters
export function useSelectedCharacters() {
  const { selectedCharacters, toggleCharacterSelection } = useCharacters();
  return { selectedCharacters, toggleCharacterSelection };
}

// Custom hook for character actions
export function useCharacterActions() {
  const { createCharacter, updateCharacter, fetchCharacters } = useCharacters();
  return { createCharacter, updateCharacter, fetchCharacters };
}